/**
 * Queue Service
 *
 * Client wrapper for the GIF upload + retrieval-analysis retry callables.
 * Upload stores the GIF; the create trigger runs the unified retrieval
 * analysis in the background. `retryAnalysis` re-runs it when the trigger
 * failed or never fired.
 */

import { httpsCallable } from 'firebase/functions'

import { functions } from '../../../firebase'
import type {
  QueueGifUploadInput,
  QueueGifUploadOptions,
  QueueGifUploadResult,
} from '../../../types'
import type {
  RetryGifAnalysisRequest,
  RetryGifAnalysisResponse,
  DeleteGifCompletelyRequest,
  DeleteGifCompletelyResponse,
} from '../../../../functions/src/shared/types/gif'

interface QueueGifUploadRequest {
  input: QueueGifUploadInput
  options?: QueueGifUploadOptions
}

interface QueueGifUploadResponse {
  success: boolean
  data?: QueueGifUploadResult
  error?: string
}

export class QueueService {
  /**
   * Queue a GIF upload.
   * Converts and stores the GIF; retrieval analysis runs in the background.
   */
  static async queueUpload(
    input: QueueGifUploadInput,
    options?: QueueGifUploadOptions
  ): Promise<QueueGifUploadResult> {
    const queueGifUpload = httpsCallable<
      QueueGifUploadRequest,
      QueueGifUploadResponse
    >(functions, 'queueGifUpload')

    const result = await queueGifUpload({ input, options })

    if (!result.data.success || !result.data.data) {
      throw new Error(result.data.error || 'Failed to queue GIF upload')
    }

    return result.data.data
  }

  /**
   * Re-run the unified retrieval analysis on a gif whose create-trigger
   * analysis failed or never fired. Admin-gated.
   */
  static async retryAnalysis(
    gifId: string
  ): Promise<NonNullable<RetryGifAnalysisResponse['data']>> {
    const retryGifAnalysis = httpsCallable<
      RetryGifAnalysisRequest,
      RetryGifAnalysisResponse
    >(functions, 'retryGifAnalysis')

    const result = await retryGifAnalysis({ gifId })

    if (!result.data.success || !result.data.data) {
      throw new Error(result.data.error || 'Failed to retry GIF analysis')
    }

    return result.data.data
  }

  /**
   * Fully delete a gif: the `gifs` doc, its paired `gifRetrieval` record, and
   * the Storage GIF + MP4 binaries. Admin-gated server-side. This goes through a
   * callable (not a direct Firestore delete) because firestore.rules forbids the
   * client from deleting a `completed` gif — and it also cleans up the retrieval
   * record so the recommendation pipeline never surfaces a deleted gif. There is
   * no undo; the caller is responsible for the confirmation prompt.
   */
  static async deleteGifCompletely(
    gifId: string
  ): Promise<NonNullable<DeleteGifCompletelyResponse['data']>> {
    const deleteGifCompletely = httpsCallable<
      DeleteGifCompletelyRequest,
      DeleteGifCompletelyResponse
    >(functions, 'deleteGifCompletely')

    const result = await deleteGifCompletely({ gifId })

    if (!result.data.success || !result.data.data) {
      throw new Error(result.data.error || 'Failed to delete GIF')
    }

    return result.data.data
  }
}
